import { useContext } from 'react';

import { OptimizelyContext, setLogLevel } from '@optimizely/react-sdk';

import {
  getOptimizelyFeatureEnabled,
  isTruthy,
  OptimizelyFeatureFlagT,
  OptimizelyOptionsT,
  viteOptimizelyMockingEnabled,
} from '@consumer-frontend/nc-utils';

export type UseOptimizelyFeatureT = (
  featureKey: OptimizelyFeatureFlagT,
  options?: OptimizelyOptionsT,
) => boolean;

// LogLevel options, from noisiest to quietest: debug, info, warn, error, null.
setLogLevel(import.meta.env.VITE_ENV === 'development' ? 'warn' : 'error');

/**
 * Resolves whether a feature flag is enabled. Reads from an Optimizely client provided via context
 * when available, falling back to a dev-only mock decision. Delegates to `getOptimizelyFeatureEnabled`
 * (passing its context-sourced client) so the hook and non-hook call paths share one implementation.
*/
export const useOptimizelyFeature: UseOptimizelyFeatureT = (featureKey, options) => {
  const { optimizely } = useContext(OptimizelyContext);
  const enabled = getOptimizelyFeatureEnabled(featureKey, options, optimizely);

  // Only the mock branch (no client from context) needs dev-visibility logging.
  if (!isTruthy(optimizely) && viteOptimizelyMockingEnabled) {
    console.info(`Returning mock decision for feature "${featureKey}"`, { enabled });
  }

  return enabled;
};
